import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GUTTER } from './paneTreeModel';

const KEY_STEP = 0.02;
const KEY_STEP_BIG = 0.1;

/**
 * The draggable gutter between the two halves of one split. `d` is a divider
 * from `computeRects` (its own rect plus the split's `span`). Pointer capture
 * keeps the drag alive when the pointer leaves the window; ratio updates are
 * batched per animation frame; double-click (or Home/Enter) resets to 50 %.
 * Keyboard: arrows move 2 %, Shift+arrow 10 %.
 */
export default function PaneDivider({ d, style, onRatio, onReset, onDragChange }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const raf = useRef(0);
  const pending = useRef(null);
  const row = d.dir === 'row';

  const ratioAt = (e) => {
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    const pos = row ? e.clientX - rect.left - d.span.x : e.clientY - rect.top - d.span.y;
    const size = (row ? d.span.w : d.span.h) - GUTTER;
    return size > 0 ? (pos - GUTTER / 2) / size : d.ratio;
  };

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setActive(true);
    onDragChange?.(true);
  };
  const onPointerMove = (e) => {
    if (!active) return;
    pending.current = ratioAt(e);
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      if (pending.current != null) onRatio(pending.current);
    });
  };
  const end = (e) => {
    if (!active) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setActive(false);
    onDragChange?.(false);
  };

  const onKeyDown = (e) => {
    const step = e.shiftKey ? KEY_STEP_BIG : KEY_STEP;
    const back = row ? 'ArrowLeft' : 'ArrowUp';
    const fwd = row ? 'ArrowRight' : 'ArrowDown';
    if (e.key === back || e.key === fwd) {
      e.preventDefault();
      e.stopPropagation();
      onRatio(d.ratio + (e.key === fwd ? step : -step));
    } else if (e.key === 'Home' || e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onReset();
    }
  };

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation={row ? 'vertical' : 'horizontal'}
      aria-valuenow={Math.round(d.ratio * 100)}
      aria-valuemin={10}
      aria-valuemax={90}
      aria-label={t('terminalExt.resizePanes', 'Resize panes')}
      className={`term-divider term-divider--${d.dir}${active ? ' term-divider--active' : ''}`}
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
      data-split-id={d.id}
    />
  );
}
